/* Mark people present — the usher at the door.
 *
 * Opens on the service that is on now. The roll is the day's own people,
 * not yet marked first; a search reaches anybody in the church, family
 * members included, so a visitor from another congregation's household is
 * one name away. A tap marks, a second tap takes it back.
 */
import { useEffect, useMemo, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useMark, useOccurrences, useRoll } from '@/api/queries'
import { Avatar } from '@/components/ui/avatar'
import { Card } from '@/components/ui/card'
import { SelectField } from '@/components/ui/field'
import { cn } from '@/lib/utils'
import { useDebounced } from '@/lib/useDebounced'
import type { Occurrence, RollRow } from '@/types/api'
import { Pager, SearchBox, StatusChip, clock, day, familyNote, todayIso } from './parts'

const PAGE_SIZE = 25

type Show = 'all' | 'not_marked' | 'present'

/** The day an usher most likely means: on now, else today, else the latest. */
export function likelyDay(items: Occurrence[]): Occurrence | undefined {
  const today = todayIso()
  return (
    items.find((o) => o.state === 'on') ??
    items.find((o) => o.date === today) ??
    items.find((o) => o.state === 'over') ??
    items[0]
  )
}

export function MarkPresent({
  churchId,
  initialDay,
}: {
  churchId: string
  /** The day a link asked for; otherwise the one most likely meant. */
  initialDay?: string | null
}) {
  const days = useOccurrences(churchId, { pageSize: 30 })
  const items = useMemo(() => days.data?.items ?? [], [days.data])
  const [chosen, setChosen] = useState<string>('')
  useEffect(() => {
    if (chosen || !items.length) return
    const asked = initialDay ? items.find((o) => o.id === initialDay) : undefined
    setChosen(asked?.id ?? likelyDay(items)?.id ?? '')
  }, [chosen, items, initialDay])

  if (days.isLoading) {
    return (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  }
  if (days.isError) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(days.error as Error).message}
      </Card>
    )
  }
  if (!items.length) {
    return (
      <Card className="p-s8 text-center text-base text-ink-2">
        No service days yet. Once a weekly service is set up, each day it
        happens appears here.
      </Card>
    )
  }

  return (
    <div className="space-y-s3">
      <SelectField
        label="Service day"
        value={chosen}
        onChange={(e) => setChosen(e.target.value)}
        options={items.map((o) => ({
          value: o.id,
          label: `${o.name} · ${day(o.date)}${o.state === 'on' ? ' · on now' : ''}`,
        }))}
      />
      {chosen ? <Roll key={chosen} churchId={churchId} occurrenceId={chosen} /> : null}
    </div>
  )
}

function Roll({ churchId, occurrenceId }: { churchId: string; occurrenceId: string }) {
  const [query, setQuery] = useState('')
  const [show, setShow] = useState<Show>('all')
  const [page, setPage] = useState(1)
  const [slotId, setSlotId] = useState('')
  const q = useDebounced(query.trim())
  useEffect(() => setPage(1), [q, show])

  const roll = useRoll(churchId, occurrenceId, { q: q || undefined, show, page, pageSize: PAGE_SIZE })
  const mark = useMark(churchId, occurrenceId)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const data = roll.data
  const c = data?.counts

  function toggle(row: RollRow) {
    setPendingId(row.id)
    mark.mutate(
      { userId: row.id, present: row.status !== 'present', slotId: slotId || null },
      { onSettled: () => setPendingId(null) },
    )
  }

  return (
    <div className="space-y-s3">
      {c ? (
        <Card className="flex flex-wrap gap-x-s6 gap-y-s2 p-s4">
          <Count value={c.present} label="present" tone="success" />
          <Count value={c.expected} label="expected" />
          <Count value={c.not_yet} label="not yet" />
          {c.visitors ? <Count value={c.visitors} label="visitors" /> : null}
        </Card>
      ) : null}

      <Card className="space-y-s3 p-s3">
        <SearchBox
          value={query}
          onChange={setQuery}
          label="Search the roll"
          placeholder="Search anyone in the church"
        />
        <div className="flex flex-wrap items-center gap-s2">
          {(
            [
              ['all', 'Everyone'],
              ['not_marked', 'Not yet'],
              ['present', 'Present'],
            ] as [Show, string][]
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={show === k}
              onClick={() => setShow(k)}
              className={cn(
                'rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
                show === k
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-line-2 text-ink-2 hover:bg-hover',
              )}
            >
              {label}
            </button>
          ))}
          {data && data.slots.length > 1 ? (
            <select
              aria-label="Which time"
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              className="ml-auto h-9 rounded border border-line-2 bg-surface px-s2 text-sm"
            >
              <option value="">The time on now</option>
              {data.slots.map((s) => (
                <option key={s.id} value={s.id}>
                  {clock(s.starts_at)} {s.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </Card>

      {mark.isError ? (
        <p role="alert" className="rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger">
          {(mark.error as Error).message}
        </p>
      ) : null}

      {roll.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : roll.isError ? (
        <Card className="p-s6 text-center text-base text-ink-2">
          {(roll.error as Error).message}
        </Card>
      ) : data && !data.items.length ? (
        <Card className="p-s6 text-center text-base text-ink-2">
          {q ? `Nobody matches “${q}”.` : 'Nobody here.'}
        </Card>
      ) : data ? (
        <Card className={cn('divide-y divide-line-2 overflow-hidden', roll.isFetching && 'opacity-70')}>
          {data.items.map((r) => {
            const present = r.status === 'present'
            const note = familyNote(r.household_of, r.relation)
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => toggle(r)}
                disabled={pendingId === r.id}
                aria-pressed={present}
                className={cn(
                  'flex w-full items-center gap-s3 px-s4 py-s3 text-left transition-colors hover:bg-hover',
                  present && 'bg-success-soft/50',
                )}
              >
                <Avatar name={r.name} src={r.avatar_url} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-semibold">{r.name}</span>
                  <span className="block truncate text-sm text-ink-2">
                    {[
                      note,
                      present && r.marked_at
                        ? `${clock(r.marked_at)}${r.slot_label ? ` · ${r.slot_label}` : ''}`
                        : null,
                      r.status === 'excused' ? 'Excused' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || (r.expected ? '' : 'Not expected today')}
                  </span>
                </span>
                {pendingId === r.id ? (
                  <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-hidden />
                ) : present ? (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-pill bg-success text-white">
                    <Check className="h-4 w-4" aria-label="Present" />
                  </span>
                ) : (
                  <StatusChip status={r.status === 'pending' ? 'pending' : r.status} />
                )}
              </button>
            )
          })}
        </Card>
      ) : null}

      {data ? (
        <Pager page={page} total={data.total} pageSize={PAGE_SIZE} onPage={setPage} />
      ) : null}
    </div>
  )
}

function Count({
  value,
  label,
  tone,
}: {
  value: number
  label: string
  tone?: 'success'
}) {
  return (
    <div>
      <div className={cn('text-lg font-extrabold tracking-tight', tone === 'success' && 'text-success-ink')}>
        {value}
      </div>
      <div className="text-sm text-ink-2">{label}</div>
    </div>
  )
}
